import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Button,
  Container,
  Flex,
  Group,
  LoadingOverlay,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { useForm, zodResolver } from '@mantine/form';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import {
  addBookLessonAudio,
  createLesson,
  getBookAllLessons,
  getLessonById,
  updateLesson,
} from '@/entities/books/api/book-lessons-api';
import { BookLessons } from '@/entities/books/model/types';
import { useDirtyForm } from '@/shared/hooks/use-dirty-form';
import { useProjectPath } from '@/shared/projects/project-path';

import styles from './book-image.module.scss';

const lessonFormSchema = z.object({
  bookId: z.number(),
  title: z.string().min(1, 'Название не может быть пустым'),
  description: z.string().optional(),
  audioUrl: z.string().min(1, 'Аудиофайл обязателен'),
  index: z.number(),
  duration: z.union([z.string(), z.number()]).transform((val) => {
    if (typeof val === 'string') {
      const [minutes, seconds] = val.split(':').map(Number);
      return (minutes || 0) * 60 + (seconds || 0);
    }
    return val;
  }),
  displayDuration: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true; // Optional field
        const timeRegex = /^\d{1,2}:\d{2}$/;
        if (!timeRegex.test(val)) return false;
        const [minutes, seconds] = val.split(':').map(Number);
        return minutes >= 0 && seconds >= 0 && seconds < 60;
      },
      {
        message:
          'Формат должен быть ММ:СС (например, 13:45), секунды не больше 59',
      },
    ),
});

type LessonFormType = BookLessons & {
  displayDuration?: string;
};

const initialLessonInfo: LessonFormType = {
  bookId: -1,
  title: '',
  description: '',
  audioUrl: '',
  index: 0,
  duration: 0,
  displayDuration: '00:00',
};

export const BookLessonDetailPage = () => {
  const [lessonInfo, setLessonInfo] = useState<BookLessons>(initialLessonInfo);
  const navigate = useNavigate();
  const withProject = useProjectPath();
  const { bookId, lessonId } = useParams();
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedAudioUrl, setUploadedAudioUrl] = useState<string>('');
  const [calculatedDuration, setCalculatedDuration] = useState<number>(0);
  const mainAudioDropzoneRef = useRef<() => void>(null);
  const isNewLesson = !lessonId || lessonId === 'new';

  const headerTitle = isNewLesson ? 'Добавление урока' : 'Редактирование урока';

  const form = useForm<LessonFormType>({
    validateInputOnBlur: true,
    initialValues: initialLessonInfo,
    validate: zodResolver(lessonFormSchema),
  });

  useDirtyForm(form.isDirty());

  useEffect(() => {
    const fetchLessons = async () => {
      if (bookId && isNewLesson) {
        const { lessons } = await getBookAllLessons(bookId);

        if (lessons) {
          const nextIndex = lessons.length + 1;

          setLessonInfo((prevState) => ({
            ...prevState,
            index: nextIndex,
          }));
          form.setFieldValue('index', nextIndex);
        }
      }
    };

    fetchLessons();
  }, [bookId, form, isNewLesson]);

  useEffect(() => {
    if (!isNewLesson && lessonId) {
      const fetchLessonData = async () => {
        try {
          const lessonResponse = await getLessonById(lessonId);
          setLessonInfo(lessonResponse);
          form.setValues({
            ...lessonResponse,
            displayDuration: formatDurationForDisplay(
              lessonResponse.duration || 0,
            ),
          });
          if (lessonResponse.audioUrl) {
            setAudioUrl(lessonResponse.audioUrl);
            setUploadedAudioUrl(lessonResponse.audioUrl);
            setCalculatedDuration(lessonResponse.duration || 0);
          }
        } catch (error) {
          console.error('Ошибка при загрузке урока:', error);
        }
      };
      fetchLessonData();
    }
  }, [lessonId, isNewLesson]);

  const uploadAudio = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append('audio', file);

      try {
        if (bookId) {
          formData.append('bookId', bookId);
          const { url } = await addBookLessonAudio(formData);

          if (!url) {
            throw new Error('Ошибка загрузки аудиофайла');
          }
          setLessonInfo((prevState) => ({
            ...prevState,
            audioUrl: url,
          }));
          return url;
        }
      } catch (error) {
        console.error('Ошибка при загрузке аудиофайла:', error);

        return '';
      }
    },
    [bookId],
  );

  const handleAudioDrop = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (file) {
        console.log('Uploading file:', file.name, 'Size:', file.size);
        setIsUploading(true);
        setAudioFile(null);
        setAudioUrl('');
        setUploadedAudioUrl('');

        try {
          const uploadedUrl = await uploadAudio(file);
          if (uploadedUrl) {
            const duration = await calculateAudioDuration(file);
            setAudioFile(file);
            setAudioUrl(URL.createObjectURL(file));
            setUploadedAudioUrl(uploadedUrl);
            setCalculatedDuration(duration);
            const formattedDuration = `${Math.floor(duration / 60)}:${String(
              Math.floor(duration % 60),
            ).padStart(2, '0')}`;
            form.setFieldValue('audioUrl', uploadedUrl);
            form.setFieldValue('displayDuration', formattedDuration);
            form.setFieldValue('duration', Math.round(duration));
            console.log(
              'Upload successful:',
              uploadedUrl,
              'Duration:',
              duration,
            );
          } else {
            console.error('Upload failed');
          }
        } catch (error) {
          console.error('Upload error:', error);
        } finally {
          setIsUploading(false);
        }
      }
    },
    [form, uploadAudio],
  );

  const calculateAudioDuration = useCallback((file: File): Promise<number> => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.onloadedmetadata = () => {
        resolve(audio.duration);
      };
      audio.onerror = () => {
        resolve(0);
      };
      audio.src = URL.createObjectURL(file);
    });
  }, []);

  const formatDurationForDisplay = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
  }, []);

  const handleSubmit = async (values: typeof form.values) => {
    const isAudioChanged = audioFile && audioFile.name !== lessonInfo.audioUrl;

    const formattedData: BookLessons = {
      lessonId: isNewLesson ? undefined : Number(lessonId),
      bookId: Number(bookId),
      title: values.title,
      description: values.description || '',
      index: values.index,
      audioUrl: isNewLesson
        ? lessonInfo.audioUrl
        : isAudioChanged
          ? lessonInfo.audioUrl
          : null,
      duration: values.displayDuration
        ? (() => {
            const [minutes, seconds] = values.displayDuration
              .split(':')
              .map(Number);
            return (minutes || 0) * 60 + (seconds || 0);
          })()
        : calculatedDuration || values.duration,
    };

    try {
      if (isNewLesson) {
        await createLesson(formattedData);
      } else {
        await updateLesson(formattedData);
      }
      await navigate(withProject(`/books/${bookId}`));
    } catch (error) {
      console.error('Ошибка при сохранении книги:', error);
    }
  };

  const handleCancel = () => {
    navigate(withProject(`/books/${bookId}`));
  };

  return (
    <Container size={1400} mt={50}>
      <Title order={1} mb={24}>
        {headerTitle}
      </Title>
      <LoadingOverlay
        visible={false}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />
      <Stack>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <TextInput
            mb={20}
            label="Название урока"
            key={form.key('title')}
            placeholder="Введите название урока"
            {...form.getInputProps('title')}
            required
          />
          <NumberInput
            mb={20}
            label="Порядковый номер урока"
            key={form.key('index')}
            min={1}
            step={1}
            clampBehavior="strict"
            placeholder="Введите номер урока"
            {...form.getInputProps('index')}
            required
          />
          <TextInput
            mb={20}
            label="Продолжительность урока"
            key={form.key('displayDuration')}
            placeholder={
              uploadedAudioUrl
                ? 'Отредактируйте при необходимости'
                : 'Продолжительность будет рассчитана автоматически'
            }
            {...form.getInputProps('displayDuration')}
            readOnly={!uploadedAudioUrl}
            disabled={!uploadedAudioUrl}
            description={
              'Посчитается автоматически, после загрузки файла можно отредактировать вручную'
            }
          />
          {uploadedAudioUrl && calculatedDuration > 0 && (
            <Text size="sm" c="blue" mb={20}>
              Проверьте, что продолжительность посчитана правильно и
              отредактируйте при необходимости ☝️
            </Text>
          )}
          {/*<Textarea*/}
          {/*  mb={20}*/}
          {/*  label="Описание"*/}
          {/*  key={form.key('description')}*/}
          {/*  name="description"*/}
          {/*  placeholder="Введите текст"*/}
          {/*  {...form.getInputProps('description')}*/}
          {/*  autosize*/}
          {/*  minRows={3}*/}
          {/*  resize="none"*/}
          {/*/>*/}

          <Title order={5}>Аудиофайл</Title>
          <Flex direction="row" gap={32} align="center">
            <Dropzone
              onDrop={handleAudioDrop}
              accept={['audio/mpeg', 'audio/mp3']}
              maxSize={300 * 1024 * 1024}
              openRef={mainAudioDropzoneRef}
              multiple={false}
              loading={isUploading}
            >
              <Flex direction="column" align="center" gap={12}>
                {isUploading ? (
                  <>
                    <Text>Загрузка...</Text>
                    <Text size="sm" c="dimmed">
                      Пожалуйста, подождите
                    </Text>
                  </>
                ) : (
                  <>
                    <Text>Перетащите файл или нажмите для выбора</Text>
                    <Text size="sm" c="dimmed">
                      Загрузить аудио
                    </Text>
                  </>
                )}
              </Flex>
            </Dropzone>
          </Flex>
          <Flex direction="column" mb={20} mt={8}>
            <Text span color="#8E8E8E">
              Допустимые форматы файлов: mp3
            </Text>
            <Text span color="#8E8E8E">
              Размер файла не должен превышать 300 МБ
            </Text>
          </Flex>

          <Flex direction="row" gap={50} align="center">
            {(audioFile || audioUrl) && !isUploading && uploadedAudioUrl && (
              <Flex direction="column" align="center" gap={8}>
                <Flex
                  direction="row"
                  align="center"
                  gap={8}
                  maw="250px"
                  w="100%"
                >
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <audio controls src={audioUrl}>
                    Ваш браузер не поддерживает аудио элемент.
                  </audio>
                  <ActionIcon
                    variant="outline"
                    onClick={() => {
                      setAudioFile(null);
                      setAudioUrl('');
                      setUploadedAudioUrl('');
                      setCalculatedDuration(0);
                      form.setFieldValue('audioUrl', '');
                      form.setFieldValue('duration', 0);
                      form.setFieldValue('displayDuration', '00:00');
                    }}
                    className={styles['clear-icon']}
                  >
                    <XMarkIcon width={20} height={20} />
                  </ActionIcon>
                </Flex>
              </Flex>
            )}
          </Flex>

          <Group my={32}>
            <Button type="submit" disabled={!uploadedAudioUrl || isUploading}>
              Сохранить
            </Button>
            <Button onClick={handleCancel} variant="outline" color="grey">
              Отменить
            </Button>
          </Group>
        </form>
      </Stack>
    </Container>
  );
};
